import { CandleChangeProto, Outlook } from "../../proto/generated/candle-data-depot-option-chain"
import { Expiry, OptionGreeks, TimeBucket } from "../../proto/generated/common"
import { OrderType } from "../../proto/generated/order"
import { Security } from "../../proto/generated/security"

export type Strike = {
    strike: number
    callOption: OptionSecurity | undefined,
    putOption: OptionSecurity | undefined,
}

export type OptionSecurity = {
    security: Security
    ltp: number
    ltpChange: number
    ltpChangePercent: number
    topBid: number
    topAsk: number
    volume: number
    openInterest: number
    impliedVolatility: number
    optionGreeks: OptionGreeks
    oiChanges: OIChange
    spanMargin?: number | undefined
    exposureMargin?: number | undefined
}

export type OIChange = {
    oiChange: {
        change: number;
        changePercent: number;
    }
    outlook: Outlook
}

export type CustomOptionSecurityType = OptionSecurity & {
    candleChange?: CandleChangeProto | undefined;
    oiRank?: number
    oiRankChange?: number
    oiChangeScore?: number
    orderType?: OrderType
    security: Security | undefined
    illiquid: boolean
}

export const OutlookToName: {
    [key: string]: string
    OUTLOOK_UNKNOWN: string
    LONG_BUILDUP: string
    LONG_UNWINDING: string
    SHORT_BUILDUP: string
    SHORT_COVERING: string
    UNRECOGNIZED: string
} = {
    OUTLOOK_UNKNOWN: 'Unknown',
    LONG_BUILDUP: 'LB',
    LONG_UNWINDING: 'LU',
    SHORT_BUILDUP: 'SB',
    SHORT_COVERING: 'SC',
    UNRECOGNIZED: 'Unrecorgnized'
}

export const optionGreeksCols = ['delta', 'theta']

export type futureOrderDataType = {
    key: string,
    security: Security,
    expiry: number,
    numberOfDaysToExpiry: string,
    derived: boolean
}

export type futureTradeDataType = {
    key: string,
    expiry: number,
    security: Security,
    numberOfDaysToExpiry: string,
    derived: boolean,
    timestamp: number,
    bid: number,
    ask: number,
    volume: number,
    openInterest: number,
    impliedVolatility: number,
    ltp: number,
    ltpChange: number,
    oiChange: number,
    lptChangePercent: number,
    candleChange?: CandleChangeProto | undefined;
}

export interface optionTradeDataType {
    key: string
    callOption: CustomOptionSecurityType | undefined
    putOption: CustomOptionSecurityType | undefined
    itmOption?: CustomOptionSecurityType | undefined
    otmOption?: CustomOptionSecurityType | undefined
    expiry: string
    timeStamp: number
    strikeCol: {
        pcr: number
        strike: number
        isMaxPain?: boolean
        maxPain?: number
        pcrStrength: number
        numberOfDaysToExpiry: string
    }
}

interface expiration {
    expiry: Expiry | undefined
    expirationDate: string
    weeklyExpiry: boolean
    name: string
}

export interface optionsTradesDataType {
    key: string
    expiration: expiration
    expirationDateKey: string
    numberOfDaysToExpiry: string
    trades: optionTradeDataType[]
    expirySeconds: string | undefined
    loading: boolean,
    strikes: Strike[]
    maxPain: number
    derived: boolean
    atmStrike: number
    ivxExpiry: number
    pcrExpiry: number
    standardDeviation: number
    oneSd: number
    minusOneSd: number
    twoSd: number
    minusTwoSd: number
    timebucket: TimeBucket
}

export const columnOptions = [
    { value: 'ltp', label: 'LTP (Chg%)' },
    { value: 'bidAsk', label: 'Bid/Ask', subValues: ['bid', 'ask'] },
    { value: 'volume', label: 'Volume' },
    { value: 'impliedVolatility', label: 'Imp. Vol.' },
    { value: 'delta', label: 'Delta (δ)' },
    { value: 'gamma', label: 'Gamma (Γ)' },
    { value: 'vega', label: 'Vega (ν)' },
    { value: 'theta', label: 'Theta(Θ)' },
    { value: 'rho', label: 'Rho (ρ)' },
    { value: 'openInterest', label: 'OI' },
    { value: 'oiChange|outlook', label: 'OI Chg | Outlook' }
]

export const spanCols = ['oiChange|outlook']
export const colsWidth: Record<string, number> = {
    'outlook': 0.5,
    'ltp': 1.5
}